"use client";

import { useActionState, startTransition } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { emailSchema } from "@src/validation/schemas";
import { Input } from "@src/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@src/components/ui/form";
import { forgotPasswordAction, type ActionState, type ForgotPasswordInput } from "../../../actions";
import { paths } from "@src/utils";
import { EXAMPLE_EMAIL } from "@src/constants";
import Link from "next/link";
import { AuthCard, AuthButton, FormAlert, OTPForm } from "../../../_components";
const formSchema = z.object({
  email: emailSchema
});
export default function RecoverForm() {
  const [state, formAction, isPending] = useActionState<ActionState, ForgotPasswordInput>(forgotPasswordAction, {
    result: "idle"
  });
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: ""
    }
  });

  // Form submission handler
  function onSubmit(values: z.infer<typeof formSchema>) {
    startTransition(() => {
      formAction({
        email: values.email
      });
    });
  }

  // Show OTP form if password reset was successful
  const showOtpForm = state.result === "success" && state.email;
  return <div className={"flex flex-col"} data-sentry-component="RecoverForm" data-sentry-source-file="recover-form.tsx">
      <AuthCard title="Forgot password" description={showOtpForm ? "Enter the 6-digit code from your email" : "Enter your email below"} footer={!showOtpForm && <p className="text-sm text-muted-foreground">
              Remember your password?{" "}
              <Link href={paths.signIn()} className="text-primary font-medium hover:underline">
                Sign in
              </Link>
            </p>} data-sentry-element="AuthCard" data-sentry-source-file="recover-form.tsx">
        {showOtpForm && state.email ? <div className="space-y-6">
            <div className="p-3 bg-blue-100 text-blue-700 rounded-md text-center text-sm">
              Check your email for a confirmation link, or enter the 6-digit code below to continue.
            </div>
            <OTPForm email={state.email} redirect={paths.resetPassword} type="recovery" />
          </div> : <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6" noValidate>
              <FormField control={form.control} name="email" render={({
            field
          }) => <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder={EXAMPLE_EMAIL} {...field} ref={undefined} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />

              <AuthButton isPending={isPending} text="Reset" pendingText="Sending..." data-ph-capture-attribute-action="auth-recover-submit" />

              <FormAlert state={state} isPending={isPending} />
            </form>
          </Form>}
      </AuthCard>
    </div>;
}